/**
 * Allow-list of custom RUM events. Names and attribute keys not listed here
 * are dropped, and values must pass the validator, so arbitrary or personal
 * data cannot be sent. The list is pending agreement with product.
 */
type AttributeValidator = (value: unknown) => boolean;

/** Report kinds a merchant can download. Closed set: a free token could carry a card number or phone. */
export const REPORT_TYPES = ['transactions', 'settlements', 'partner-merchants'] as const;
export type ReportType = (typeof REPORT_TYPES)[number];

const oneOf =
  (allowed: readonly string[]): AttributeValidator =>
  (v) =>
    typeof v === 'string' && allowed.includes(v);
const reference: AttributeValidator = (v) => typeof v === 'string' && /^[0-9A-F]{8}$/.test(v);
const traceId: AttributeValidator = (v) => typeof v === 'string' && /^[0-9a-f]{32}$/.test(v);
const httpMethod: AttributeValidator = (v) =>
  typeof v === 'string' && /^(GET|POST|PUT|PATCH|DELETE|HEAD|OPTIONS)$/.test(v);
const count: AttributeValidator = (v) => typeof v === 'number' && Number.isFinite(v) && v >= 0;

export const TELEMETRY_EVENTS = {
  report_downloaded: { reportType: oneOf(REPORT_TYPES) },
  refund_started: {},
  user_invited: {},
  error_shown: { reference: reference },
  http_request: { traceId: traceId, method: httpMethod, status: count, durationMs: count },
} as const satisfies Record<string, Record<string, AttributeValidator>>;

export type TelemetryEventName = keyof typeof TELEMETRY_EVENTS;

export type TelemetryEventAttributes = {
  report_downloaded: { reportType: ReportType };
  refund_started: Record<string, never>;
  user_invited: Record<string, never>;
  error_shown: { reference: string };
  http_request: { traceId: string; method: string; status: number; durationMs: number };
};

/** Returns the validated attributes, or null if the event must not be sent. */
export function sanitiseEvent(
  name: string,
  attributes: Record<string, unknown>,
): Record<string, string | number> | null {
  if (!Object.prototype.hasOwnProperty.call(TELEMETRY_EVENTS, name)) {
    return null;
  }
  const schema = TELEMETRY_EVENTS[name as TelemetryEventName] as Record<string, AttributeValidator>;
  const result: Record<string, string | number> = {};
  for (const key of Object.keys(schema)) {
    const value = attributes[key];
    if (!schema[key](value)) {
      return null;
    }
    result[key] = value as string | number;
  }
  return result;
}
